import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import ForwardIcon from '@mui/icons-material/Forward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from '@mui/styles';

import { CandidateField } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { CareTeamCandidate } from '@vestahealthcare/common/models';
import { EMPTY } from '@vestahealthcare/common/utils/constants';

import {
  Button,
  CollapseIcon,
  Colors,
  IconButton,
  Panel,
  PhoneLink,
  Table,
  UpdatedBy,
} from 'styleguide-v2';

type Props = {
  className?: string;
  candidates: CareTeamCandidate[];
  onCreate: (candidate: CareTeamCandidate) => void;
  onCreateCommunityResource: (candidate: CareTeamCandidate) => void;
  onViewInfo: (candidate: CareTeamCandidate) => void;
  onDelete: (candidate: CareTeamCandidate) => void;
};

const CARE_TEAM_CANDIDATE_COLUMNS = (
  { onCreate, onCreateCommunityResource, onDelete, onViewInfo }: Props,
  active: boolean,
) => [
  {
    headerName: translate('careTeam.common.name'),
    component: ({ fullName }: CareTeamCandidate) => fullName,
    width: 175,
  },
  {
    headerName: translate('careTeam.common.subrole'),
    component: ({ candidateFieldValues }: CareTeamCandidate) =>
      candidateFieldValues?.find(
        ({ field }) => field === CandidateField.SUBTYPE_RELATIONSHIP_SPECIALITY,
      )?.value || EMPTY,
    width: 175,
  },
  {
    headerName: translate('careTeam.common.phones'),
    component: ({ id, candidateFieldPhonesValues }: CareTeamCandidate) => {
      if (!candidateFieldPhonesValues.length) return EMPTY;
      return candidateFieldPhonesValues
        .filter(({ number, type }) => number && type)
        .map(({ number, type }, idx) => (
          <PhoneLink
            phone={number}
            type={type}
            key={`candidate-${id}-phone-${number || idx}`}
          />
        ));
    },
    width: 175,
  },
  {
    headerName: translate('careTeam.common.emailAddress'),
    component: ({ candidateFieldValues }: CareTeamCandidate) =>
      candidateFieldValues?.find(({ field }) => field === CandidateField.EMAIL)
        ?.value || EMPTY,
    width: 200,
  },
  {
    headerName: translate('careTeam.common.addressInformation'),
    component: ({ candidateFieldValues }: CareTeamCandidate) => {
      const address = candidateFieldValues?.find(
        ({ field }) => field === CandidateField.ADDRESS_1,
      )?.value;
      const address2 = candidateFieldValues?.find(
        ({ field }) => field === CandidateField.ADDRESS_2,
      )?.value;
      const city = candidateFieldValues?.find(
        ({ field }) => field === CandidateField.CITY,
      )?.value;
      const state = candidateFieldValues?.find(
        ({ field }) => field === CandidateField.STATE,
      )?.value;
      const zipCode = candidateFieldValues?.find(
        ({ field }) => field === CandidateField.ZIP_CODE,
      )?.value;

      const addressInfo = [address, address2, city, state, zipCode].filter(
        Boolean,
      );

      return addressInfo.length ? (
        <>
          <p className="no-margin">{address}</p>
          {address2 && <p className="no-margin">{address2}</p>}
          <p className="no-margin">
            {[city, state, zipCode].filter(Boolean).join(', ')}
          </p>
        </>
      ) : (
        EMPTY
      );
    },
    width: 250,
  },
  {
    headerName: translate(`careTeam.common.${active ? 'created' : 'deleted'}`),
    component: ({ createdAt, deletedAt }: CareTeamCandidate) => (
      <UpdatedBy date={deletedAt || createdAt} />
    ),
  },
  {
    headerName: ' ',
    component: (candidate: CareTeamCandidate) =>
      active ? (
        <div className="flex gap">
          <IconButton
            size="small"
            onClick={() => onViewInfo(candidate)}
            tooltip="View Candidate Details"
          >
            <InfoOutlinedIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onCreate(candidate)}
            tooltip="Import to Care Team"
          >
            <ForwardIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onCreateCommunityResource(candidate)}
            tooltip={translate('careTeam.actions.createCommunityResource')}
          >
            <BusinessIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(candidate)}
            tooltip={translate('careTeam.actions.deleteCandidate')}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ) : (
        <></>
      ),
    width: 50,
  },
];

const useStyles = makeStyles({
  table: {
    '&&.table-empty': {
      padding: '1rem 0',
      textAlign: 'start',
    },
  },
  showInactiveButton: {
    '&&': {
      display: 'flex',
      margin: '0.5rem 0 0 auto',
    },
  },
  inactiveRow: {
    '&&': {
      backgroundColor: `${Colors.lighterGray}!important`,
    },
  },
});

export const CareTeamCandidateTable = (props: Props) => {
  const styles = useStyles();
  const { className, candidates, onDelete } = props;

  const [showInactive, setShowInactive] = useState<boolean>(false);

  const activeCandidates = candidates.filter(({ deletedAt }) => !deletedAt);
  const inactiveCandidates = candidates.filter(({ deletedAt }) => deletedAt);

  const activeColumns = useMemo(
    () => CARE_TEAM_CANDIDATE_COLUMNS(props, true),
    [
      onDelete,
      props.onCreate,
      props.onCreateCommunityResource,
      props.onViewInfo,
    ],
  );
  const inactiveColumns = useMemo(
    () => CARE_TEAM_CANDIDATE_COLUMNS(props, false),
    [
      onDelete,
      props.onCreate,
      props.onCreateCommunityResource,
      props.onViewInfo,
    ],
  );

  return (
    <>
      <Table
        className={classNames(className, styles.table)}
        config={{
          columns: activeColumns,
          compact: true,
          data: activeCandidates,
          paginationOptions: [-1],
        }}
        fontSize="small"
        empty
      />
      {!!inactiveCandidates?.length && (
        <>
          <Button
            className={styles.showInactiveButton}
            onClick={() => setShowInactive(!showInactive)}
            color="quaternary"
            size="xs"
          >
            {showInactive
              ? 'Hide Inactive Candidates'
              : 'Show Inactive Candidates'}
            <CollapseIcon open={showInactive} color="disabled" />
          </Button>
          <Panel.Collapse open={showInactive}>
            <Table
              className={classNames(className, styles.table)}
              config={{
                columns: inactiveColumns,
                compact: true,
                data: inactiveCandidates,
                getRowClass: () => styles.inactiveRow,
                paginationOptions: [-1],
              }}
              fontSize="small"
            />
          </Panel.Collapse>
        </>
      )}
    </>
  );
};

export default CareTeamCandidateTable;
